import type {ReactElement} from 'react';
import {useState} from 'react';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {Button, ButtonType, ButtonSize} from '@Components/button';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {InputField} from '@Components/input-field-v2';
import {InputFieldSize, InputFieldState} from '@Components/input-field-v2/input-field.types';
import {CheckoutMode, PROMO_DISCOUNT_TYPE_PERCENTAGE} from '@Components/checkout-flow/checkout-flow.types';
import {setPromoApplied, setPromoCode, setPromoError, setStripeSession} from '@Components/checkout-flow/checkout-flow-reducer';
import {applyDownloadPromo, validateSubscriptionPromo} from '@Components/checkout-flow/checkout-flow-thunk';
import {handleCheckoutSuccess} from '@Components/checkout-flow/checkout-flow-completion.library';
import {CheckoutInfoMessage} from '@Components/checkout-flow/components/checkout-info-message/checkout-info-message';
import {CheckoutInfoMessageTone} from '@Components/checkout-flow/components/checkout-info-message/checkout-info-message.types';
import type {PromoCodeInputProps, PromoDiscountDetails} from '@Components/checkout-flow/components/promo-code-input/promo-code-input.types';
import {PROMO_CODE_INPUT_ID, PROMO_CODE_PLACEHOLDER} from '@Components/checkout-flow/components/promo-code-input/promo-code-input.types';
import styles from './promo-code-input.module.scss';

export function PromoCodeInput({request, controller, ref}: PromoCodeInputProps): ReactElement | null {
  const dispatch = useAppDispatch();
  const promo = useAppSelector((state) => state.checkoutFlow.promo);
  const dialogData = useAppSelector((state) => state.checkoutFlow.dialogData);
  const mode = useAppSelector((state) => state.checkoutFlow.mode);
  const [isApplying, setIsApplying] = useState(false);
  const isSubscription = request.mode === CheckoutMode.SUBSCRIPTION;
  const isTrial = isSubscription && request.isTrial;

  if (!isSubscription && request.mode !== CheckoutMode.DOWNLOAD) {
    return null;
  }
  if (isSubscription && promo.isApplied) {
    return null;
  }

  const applyPromo = async (): Promise<void> => {
    const code = promo.code.trim();
    if (code === '' || isApplying) {
      return;
    }
    setIsApplying(true);
    try {
      if (request.mode === CheckoutMode.SUBSCRIPTION) {
        await validateSubscriptionPromo(code, request);
        dispatch(setPromoApplied(''));
        dispatch(setStripeSession(null));
        return;
      }
      if (request.mode === CheckoutMode.DOWNLOAD) {
        await applyPromoToDownload(code);
      }
    } catch (error) {
      dispatch(setPromoError(error instanceof Error && error.message !== '' ? error.message : window.i18next.t('pmwjs_enter_valid_promo')));
    } finally {
      setIsApplying(false);
    }
  };

  const applyPromoToDownload = async (code: string): Promise<void> => {
    if (request.mode !== CheckoutMode.DOWNLOAD || dialogData === null || mode === null) {
      return;
    }
    const data = await applyDownloadPromo({promoCode: code, request});
    if (Number(data.discountedPrice) === 0 && data.freeCheckout !== undefined) {
      handleCheckoutSuccess({
        request,
        effectiveMode: mode,
        result: data.freeCheckout,
        handlers: {
          showPremiumConfirmation: controller.showPremiumConfirmation,
          showError: (message): void => {
            controller.fail(window.i18next.t('pmwjs_payment_failed'), message);
          },
          exit: controller.exit,
        },
      });
      return;
    }
    dispatch(
      setPromoApplied(
        getDiscountText({
          discountType: data.promotion.discountType,
          discountAmount: data.promotion.discountAmount,
          beforeDiscountPrice: data.beforeDiscountPrice,
          discountedPrice: data.discountedPrice,
          currencyCode: dialogData.currencyCode,
        })
      )
    );
    dispatch(setStripeSession(null));
  };

  const getApplyControl = (): ReactElement => {
    if (promo.isApplied) {
      return (
        <div className={styles.appliedPill}>
          <Text val={promo.appliedText} size={TextSize.MD} bold className="content-success" />
        </div>
      );
    }
    return <Button type={ButtonType.SECONDARY} size={ButtonSize.LARGE} text={window.i18next.t('pmwjs_apply')} customClasses="-long" disabled={promo.code.trim() === ''} isLoading={isApplying} onClick={applyPromo} />;
  };

  const getTrialHint = (): ReactElement | null => {
    if (!isTrial) {
      return null;
    }
    return <CheckoutInfoMessage message={window.i18next.t('pmwjs_promotion_code_subhead')} />;
  };

  const getStatusMessage = (): ReactElement => {
    if (promo.errorMessage !== '') {
      return <CheckoutInfoMessage message={promo.errorMessage} tone={CheckoutInfoMessageTone.DANGER} textClassName="js-apply-promo-error" />;
    }
    if (promo.isApplied) {
      return <CheckoutInfoMessage message={window.i18next.t('pmwjs_promo_code_applied')} tone={CheckoutInfoMessageTone.SUCCESS} />;
    }
    return <CheckoutInfoMessage message={window.i18next.t('pmwjs_if_you_have_promo_please_enter_above')} />;
  };

  return (
    <div ref={ref} className={`promo-code-section ${styles.container}`}>
      <label htmlFor={PROMO_CODE_INPUT_ID}>
        <Text val={window.i18next.t('pmwjs_promo_code')} size={TextSize.SM} bold className="content-body spacing-m-b-1" />
      </label>
      <div className={styles.row}>
        <div className={`${styles.inputContainer} ${promo.isApplied ? styles.inputApplied : ''}`}>
          <Icon icon="icon-discount" size={IconSize.SIZE_ICON_16} className={`content-disabled ${styles.discountIcon}`} ariaHidden />
          <InputField
            id={PROMO_CODE_INPUT_ID}
            input={promo.code}
            placeholder={PROMO_CODE_PLACEHOLDER}
            size={InputFieldSize.MEDIUM}
            state={promo.errorMessage !== '' ? InputFieldState.ERROR : InputFieldState.DEFAULT}
            className={styles.input}
            isDisabled={promo.isApplied}
            isControlled
            onInputChange={(value): void => {
              dispatch(setPromoCode(String(value)));
            }}
            onInputSubmit={(): void => {
              void applyPromo();
            }}
          />
        </div>
        {getApplyControl()}
      </div>
      {getStatusMessage()}
      {getTrialHint()}
    </div>
  );
}

const getDiscountText = ({discountType, discountAmount, beforeDiscountPrice, discountedPrice, currencyCode}: PromoDiscountDetails): string => {
  const discount =
    discountType === PROMO_DISCOUNT_TYPE_PERCENTAGE
      ? window.PMW.util.percentageFormat(parseFloat(discountAmount))
      : window.PMW.util.currencyFormat(parseFloat(beforeDiscountPrice) - parseFloat(discountedPrice), currencyCode);
  return window.i18next.t('pmwjs_x_off', {discount});
};
